/**
 * How old the schedule on screen is allowed to get before the calendar says so.
 *
 * The thresholds below are derived from the **poll interval**, not from wall-clock intuition.
 * `useSchedule` and `useTechnicianTasks` both run `refetchInterval: SCHEDULE_POLL_MS`, so a healthy
 * calendar is never more than about a minute old, and every threshold here is a count of missed
 * polls:
 *
 *   FRESH_MAX_MS  = 2.5 missed polls — one dropped tick must never turn the badge amber
 *   AGING_MAX_MS  = 10 missed polls  — by here the device has been out of touch long enough that
 *                                      the plan on screen may genuinely no longer be the plan
 *
 * They are multiples of the shared constant rather than literals, so changing the poll interval
 * moves them with it instead of silently invalidating them.
 */

import { SCHEDULE_POLL_MS } from '../../../shared/config/polling'

export type ScheduleFreshnessLevel = 'unknown' | 'fresh' | 'aging' | 'stale' | 'disconnected'

/** Two and a half missed polls. */
export const FRESH_MAX_MS = SCHEDULE_POLL_MS * 2.5

/** Ten missed polls. */
export const AGING_MAX_MS = SCHEDULE_POLL_MS * 10

const SECOND_MS = 1_000
const MINUTE_MS = 60_000
const HOUR_MS = 3_600_000
const DAY_MS = 86_400_000

/**
 * `disconnected` outranks every age, because it names a cause the age alone cannot.
 *
 * Age says "this is old"; disconnected says "and it will stay old until something changes", which
 * is the difference between waiting and acting.
 */
export function resolveFreshnessLevel(
  ageMs: number | null,
  isDisconnected: boolean,
): ScheduleFreshnessLevel {
  if (isDisconnected) return 'disconnected'
  if (ageMs === null) return 'unknown'
  if (ageMs < FRESH_MAX_MS) return 'fresh'
  if (ageMs < AGING_MAX_MS) return 'aging'
  return 'stale'
}

/**
 * The age, in German, as it is **read**.
 *
 * Counts seconds under a minute, like the work-order badge's `Vor 12 Sek.`. That precision is not
 * a claim about the data — polled data is still up to a minute behind — it is a claim about the
 * *request*: a worker who taps refresh, or who is waiting out the poll before driving off, needs to
 * see the number move. A label that reads `Gerade eben` for 59 seconds out of every 60 is
 * indistinguishable from a frozen screen, which is the failure the badge exists to rule out.
 *
 * The cost is one re-render a second inside the badge, which `useElapsedSince` schedules and which
 * goes no further than the badge's own four nodes. What must *not* pay that cost is the screen
 * reader — see `formatScheduleAgeSpoken`.
 */
export function formatScheduleAge(ageMs: number | null): string {
  if (ageMs === null) return 'Noch nicht aktualisiert'
  if (ageMs < MINUTE_MS) return `Vor ${Math.floor(ageMs / SECOND_MS)} Sek.`
  if (ageMs < HOUR_MS) return `Vor ${Math.floor(ageMs / MINUTE_MS)} Min.`
  if (ageMs < DAY_MS) return `Vor ${Math.floor(ageMs / HOUR_MS)} Std.`
  return 'Vor über einem Tag'
}

/**
 * The same age, coarse, for the badge's `role="status"` live region.
 *
 * A polite live region is re-announced whenever its accessible name changes. Feed it
 * `formatScheduleAge` and a screen reader reads the badge aloud once a second for as long as the
 * calendar is open — the counter that reassures a sighted user would make the page unusable. At
 * minute precision the string is identical between ticks, so nothing is announced until something
 * a listener would care about has actually changed.
 */
export function formatScheduleAgeSpoken(ageMs: number | null): string {
  if (ageMs === null) return 'Noch nicht aktualisiert'
  if (ageMs < MINUTE_MS) return 'Gerade eben'
  return formatScheduleAge(ageMs)
}

/** The word beside the dot. `aging` and `stale` share one — they differ in colour, not in meaning. */
export function freshnessLabel(level: ScheduleFreshnessLevel): string {
  switch (level) {
    case 'disconnected':
      return 'Keine Verbindung'
    case 'fresh':
      return 'Aktuell'
    case 'aging':
    case 'stale':
      return 'Veraltet'
    case 'unknown':
    default:
      return 'Unbekannt'
  }
}
